import React from "react";
import {
  Location,
  getLocationPlans,
  FamilyPlanOptions,
  toCurrency,
  calculateFamilyPlanTotals,
  roundTo,
} from "@ecologi/shared";
import AdultIcon from "@ecologi/assets/icons/Plan/adult.svg";
import ChildIcon from "@ecologi/assets/icons/Plan/child.svg";
import PetIcon from "@ecologi/assets/icons/Plan/pet.svg";
import TreesIcon from "@ecologi/assets/icons/Plan/trees.svg";
import CarbonIcon from "@ecologi/assets/icons/Plan/carbon.svg";
import { NumberField } from "../../NumberField";

import styles from "../styles/PlanFamilyCard.module.scss";

type Props = {
  location: Location | null;
  isUpdateSubscription: boolean;
  selectedFamilyPlanAddons: FamilyPlanOptions;
  setSelectedFamilyPlanAddons: (options: FamilyPlanOptions) => void;
};

export const PlanFamilyCard: React.FC<Props> = (props) => {
  const {
    isUpdateSubscription,
    location,
    selectedFamilyPlanAddons,
    setSelectedFamilyPlanAddons,
  } = props;

  const { currencySymbol, familyAddonsForLocation, numTonnesPerPerson } =
    getLocationPlans(location);

  const totals = calculateFamilyPlanTotals(
    familyAddonsForLocation,
    selectedFamilyPlanAddons
  );

  const numFootprints = roundTo(1)(totals.carbon / numTonnesPerPerson);

  const hasSelectedAddons =
    selectedFamilyPlanAddons.adults > 0 ||
    selectedFamilyPlanAddons.children > 0 ||
    selectedFamilyPlanAddons.pets > 0;

  return (
    <section className={styles.card}>
      <div className={styles.planWrapper}>
        <h2 className={styles.header}>
          {isUpdateSubscription ? "Update" : "Create"} your family plan
        </h2>
        <div>
          <div
            className={styles.planSelectWrapper}
            data-testid="family-plan-adult"
          >
            <AdultIcon className={styles.planSelectIcon} />
            <div className={styles.planSelectTextNumber}>
              <div className={styles.planSelectText}>
                <h3 className={styles.planSelectTitle}>Adults</h3>
                <p className={styles.planSelectPrice}>
                  {currencySymbol === "$" ? "US$" : currencySymbol}
                  {toCurrency(familyAddonsForLocation.adults.price)} each
                </p>
              </div>
              <NumberField
                className={styles.planSelectNumberField}
                label="Adults"
                name="adults"
                min={0}
                max={20}
                value={selectedFamilyPlanAddons.adults}
                onChange={(e) => {
                  setSelectedFamilyPlanAddons({
                    ...selectedFamilyPlanAddons,
                    adults: e.target.value,
                  });
                }}
              />
            </div>
          </div>
          <div
            className={styles.planSelectWrapper}
            data-testid="family-plan-child"
          >
            <ChildIcon className={styles.planSelectIcon} />
            <div className={styles.planSelectTextNumber}>
              <div className={styles.planSelectText}>
                <h3 className={styles.planSelectTitle}>Children</h3>
                <p className={styles.planSelectPrice}>
                  {currencySymbol === "$" ? "US$" : currencySymbol}
                  {toCurrency(familyAddonsForLocation.children.price)} each
                </p>
              </div>
              <NumberField
                className={styles.planSelectNumberField}
                label="Children"
                name="children"
                min={0}
                max={20}
                value={selectedFamilyPlanAddons.children}
                onChange={(e) => {
                  setSelectedFamilyPlanAddons({
                    ...selectedFamilyPlanAddons,
                    children: e.target.value,
                  });
                }}
              />
            </div>
          </div>
          <div
            className={styles.planSelectWrapper}
            data-testid="family-plan-pet"
          >
            <PetIcon className={styles.planSelectIcon} />
            <div className={styles.planSelectTextNumber}>
              <div className={styles.planSelectText}>
                <h3 className={styles.planSelectTitle}>Pets</h3>
                <p className={styles.planSelectPrice}>
                  {currencySymbol === "$" ? "US$" : currencySymbol}
                  {toCurrency(familyAddonsForLocation.pets.price)} each
                </p>
              </div>
              <NumberField
                className={styles.planSelectNumberField}
                label="Pets"
                name="pets"
                min={0}
                max={20}
                value={selectedFamilyPlanAddons.pets}
                onChange={(e) => {
                  setSelectedFamilyPlanAddons({
                    ...selectedFamilyPlanAddons,
                    pets: e.target.value,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.planImpact}>
        <h3 className={styles.impactTitle} data-testid="impact-title">
          Your{" "}
          {hasSelectedAddons ? (
            <strong>
              {currencySymbol === "$" ? "US$" : currencySymbol}
              {totals.price} a month
            </strong>
          ) : (
            "monthly donation"
          )}{" "}
          will have this much impact:
        </h3>
        <div>
          <div
            className={styles.impactBlockTrees}
            data-testid="impact-block-trees"
          >
            <TreesIcon
              className={styles.impactIcon}
              width={64}
              height={64}
              viewBox="0 0 48 48"
            />
            <div>
              <p className={styles.totalImpact}>
                {hasSelectedAddons ? `${totals.trees} trees` : "Trees"} funded
              </p>
              <p className={styles.impactTime}>every month</p>
            </div>
          </div>
          <div
            className={styles.impactBlockCarbon}
            data-testid="impact-block-carbon"
          >
            <CarbonIcon
              className={styles.impactIcon}
              width={64}
              height={64}
              viewBox="0 0 48 48"
            />
            <div>
              <p className={styles.totalImpact}>
                {hasSelectedAddons ? `${totals.carbonAnnual} tonnes` : "Tonnes"}{" "}
                CO2
              </p>
              <p className={styles.impactTime}>
                avoided every year
                {numFootprints > 0 && (
                  <span>
                    (~{numFootprints} footprint{numFootprints === 1 ? "" : "s"}
                    *)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
